// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 3 -- Leaderboard. This is where the headline finding is ARGUED rather
// than asserted, so the shape of the decile curve has to be legible before any
// ranking is shown. Two claims are made here and both are drawn:
//
//   1. The curve is J-SHAPED, not a slope. Deciles 1-8 span 1.44x; only the top
//      two break away. "Richer postcodes get more honours" is the wrong reading,
//      and so is "poor postcodes are overlooked" -- the poorest decile out-rates
//      four of the middle ones.
//   2. The obvious age objection is REFUTED IN THE OPPOSITE DIRECTION. Per
//      resident aged 65+, the top-to-bottom ratio widens from 2.53x to 4.45x,
//      because the richest postcodes are younger than the poorest.
//
// Everything on this page is an Order of Australia measure, 2010 onwards. That
// is stated on the artefacts themselves, not just in the preamble.

import type { Ctx, Place, View } from '../types';
import { dec, escapeHtml, money, num, pct, tipAttr, tipValue } from '../format';
import { DIVERGING, RAMP } from '../palette';
import { gloss } from '../glossary';

const NS = 'http://www.w3.org/2000/svg';

const CRUDE_COLOUR = RAMP[3];
const AGED_COLOUR = RAMP[5];
const BAR_COLOUR = RAMP[4];

type SortKey = 'crude' | 'aged' | 'n' | 'surplus' | 'shortfall';

interface Row {
  pc: string;
  n: number;
  pop: number;
  o65: number;
  inc: number | null;
  age: number | null;
  crude: number;
  aged: number;
  surplus: number;
  decile: number | null;
}

interface State {
  mode: 'rate' | 'index';
  sort: SortKey;
  decile: number | null;
  limit: number;
  q: string;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

function svgText(
  x: number,
  y: number,
  s: string,
  attrs: Record<string, string | number> = {},
): SVGTextElement {
  const t = svgEl('text', { x, y, ...attrs });
  t.textContent = s;
  return t;
}

/** Nice axis step. Guards a non-positive input so no NaN reaches a coordinate. */
function niceStep(raw: number): number {
  if (!(raw > 0) || !Number.isFinite(raw)) return 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  const s = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return s * mag;
}

function axisTop(dataMax: number, intervals: number): { max: number; step: number } {
  const safeMax = Number.isFinite(dataMax) && dataMax > 0 ? dataMax : 1;
  const step = niceStep((safeMax * 1.08) / Math.max(1, intervals));
  const max = Math.ceil((safeMax * 1.08) / step) * step;
  return { max: max > 0 ? max : step, step };
}

function ticksOf(max: number, step: number): number[] {
  const out: number[] = [];
  if (!(step > 0)) return [0, max];
  for (let t = 0; t <= max + step / 1000 && out.length < 40; t += step) out.push(t);
  return out;
}

const signed = (v: number): string =>
  `${v >= 0 ? '+' : '−'}${dec(Math.abs(v), 1)}`;

/**
 * Rebuild each eligible postcode's income decile, then CHECK the rebuild against
 * the deciles the pipeline published. If a single award total disagrees the map
 * is discarded and the decile affordances are never drawn -- a filter that
 * silently disagrees with the chart above it would be worse than no filter.
 */
function assignDeciles(eligible: Place[], published: { awards: number }[]): Map<string, number> | null {
  const withInc = eligible
    .filter((p) => p.inc != null)
    .sort((a, b) => (a.inc as number) - (b.inc as number) || (a.pc < b.pc ? -1 : a.pc > b.pc ? 1 : 0));
  const n = withInc.length;
  if (n < 10 || published.length !== 10) return null;

  const base = Math.floor(n / 10);
  const map = new Map<string, number>();
  const sums = new Array<number>(10).fill(0);
  let i = 0;
  for (let d = 0; d < 10; d++) {
    const size = d < 9 ? base : n - base * 9;
    for (let k = 0; k < size && i < n; k++, i++) {
      map.set(withInc[i].pc, d + 1);
      sums[d] += withInc[i].n;
    }
  }
  return published.every((p, ix) => p.awards === sums[ix]) ? map : null;
}

export const leaderboardView: View = (root, ctx: Ctx) => {
  const { core, geo, meta } = ctx;
  const deciles = core.deciles;

  const natCrude = geo.national.pop > 0 ? (geo.national.awards / geo.national.pop) * 1e4 : 0;
  const natAged = geo.national.over65 > 0 ? (geo.national.awards / geo.national.over65) * 1e4 : 0;

  const eligible = geo.places.filter((p) => p.eligible === 1);
  const excluded = geo.places.length - eligible.length;
  const excludedAwards = geo.places.reduce((s, p) => s + (p.eligible === 1 ? 0 : p.n), 0);
  const excludedPop = geo.places.reduce((s, p) => s + (p.eligible === 1 ? 0 : p.pop), 0);

  const dmap = assignDeciles(eligible, deciles);

  // The sharpest possible argument for the floor: the postcode that would top an
  // unfiltered table, and the best eligible postcode it would beat.
  let tiny: { pc: string; n: number; pop: number; rate: number } | null = null;
  for (const p of geo.places) {
    if (p.eligible === 1 || p.pop <= 0) continue;
    const r = (p.n / p.pop) * 1e4;
    if (!tiny || r > tiny.rate) tiny = { pc: p.pc, n: p.n, pop: p.pop, rate: r };
  }
  const bestEligible = eligible.reduce(
    (m, p) => Math.max(m, p.pop > 0 ? (p.n / p.pop) * 1e4 : 0),
    0,
  );

  const rows: Row[] = eligible.map((p) => ({
    pc: p.pc,
    n: p.n,
    pop: p.pop,
    o65: p.o65,
    inc: p.inc,
    age: p.age,
    crude: p.pop > 0 ? (p.n / p.pop) * 1e4 : 0,
    aged: p.o65 > 0 ? (p.n / p.o65) * 1e4 : Number.NaN,
    surplus: p.n - (p.pop / 1e4) * natCrude,
    decile: dmap ? (dmap.get(p.pc) ?? null) : null,
  }));

  const maxCrudeRow = rows.reduce((m, r) => Math.max(m, r.crude), 0);

  // ---- headline ratios, all computed from the data rather than typed in ----
  const d1 = deciles[0];
  const d10 = deciles[deciles.length - 1];
  const ratioCrude = d1 && d1.crude > 0 ? d10.crude / d1.crude : Number.NaN;
  const ratioAged = d1 && d1.aged > 0 ? d10.aged / d1.aged : Number.NaN;
  const bottom8 = deciles.slice(0, 8).map((d) => d.crude);
  const lo8 = Math.min(...bottom8);
  const hi8 = Math.max(...bottom8);
  const spread8 = lo8 > 0 ? hi8 / lo8 : Number.NaN;
  const beaten = deciles
    .slice(0, 8)
    .filter((d) => d.decile !== 1 && d1 && d.crude < d1.crude)
    .map((d) => d.decile);

  const params = new URLSearchParams(root.dataset.params ?? '');
  const SORTS: SortKey[] = ['crude', 'aged', 'n', 'surplus', 'shortfall'];
  const askedSort = params.get('sort') as SortKey | null;
  const askedDecile = Number(params.get('d'));
  const state: State = {
    mode: params.get('mode') === 'index' ? 'index' : 'rate',
    sort: askedSort && SORTS.includes(askedSort) ? askedSort : 'crude',
    decile: dmap && askedDecile >= 1 && askedDecile <= 10 ? askedDecile : null,
    limit: 40,
    q: (params.get('q') ?? '').replace(/[^0-9]/g, '').slice(0, 4),
  };

  const scopeLine = `Order of Australia only, awards gazetted from ${geo.fromYear} to ${meta.as_at_award_date.slice(0, 4)}`;

  // ------------------------------------------------------------------ shell --
  root.innerHTML = `
<style>
  .lb-grid { display: grid; gap: var(--space-lg); grid-template-columns: repeat(auto-fit, minmax(min(420px, 100%), 1fr)); }
  .lb-chart-title { font-size: var(--font-size-base); font-weight: 600; color: var(--text-primary); margin-bottom: 2px; }
  .lb-chart-sub { font-size: var(--font-size-sm); color: var(--text-secondary); margin-bottom: var(--space-sm); }
  .lb-chart-wrap svg.chart { min-width: 420px; width: 100%; }
  .lb-dash { display: inline-block; width: 16px; height: 0; border-top: 2px dashed var(--accent-secondary); flex-shrink: 0; }
  .lb-pill { min-height: 40px; }
  .lb-th { font: inherit; font-size: var(--font-size-sm); font-weight: 600; color: var(--text-secondary);
           background: none; border: 0; padding: 0; min-height: 40px; cursor: pointer; white-space: nowrap; }
  .lb-th[aria-pressed='true'] { color: var(--accent-primary-hover); text-decoration: underline; text-underline-offset: 3px; }
  .lb-pc { font: inherit; font-family: var(--font-mono); font-size: var(--font-size-sm); font-weight: 600;
           background: none; border: 0; padding: 0.35rem 0.1rem; min-height: 40px; color: var(--accent-secondary);
           text-decoration: underline; text-underline-offset: 2px; cursor: pointer; }
  .lb-bar { display: block; height: 4px; border-radius: 2px; background: ${BAR_COLOUR}; margin: 3px 0 0 auto; }
  .lb-rowbar { min-width: 0; }
  .lb-foot { display: flex; flex-wrap: wrap; gap: var(--space-md); align-items: center; margin-top: var(--space-md); }
  .lb-count { font-size: var(--font-size-sm); color: var(--text-secondary); min-width: 0; }
  .lb-key { display: grid; gap: var(--space-xs); grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
            font-size: var(--font-size-sm); color: var(--text-secondary); margin-top: var(--space-sm); }
</style>

<div class="view-head">
  <h1>Who actually gets an Order of Australia — and where</h1>
  <p class="lede">Awards do not land evenly across the country, but the shape of the unevenness is not
    the one people expect. Sorted by income, the bottom eight-tenths of Australia receives honours at
    roughly the <em>same</em> rate; only the richest two ${gloss('decile', 'deciles')} break away. This page
    shows that curve, tests it against the obvious age objection, and then ranks every eligible postcode.</p>
  <p class="panel-sub"><strong>Scope:</strong> ${escapeHtml(scopeLine)} — ${num(geo.national.awards)}
    awards. Other honours are excluded here, including the ${gloss('national-medal', 'National Medal')} —
    the single largest share of the register, and a 15-year long-service medal rather than a
    recognition of any particular achievement.
    A postcode is published for ${pct(core.totals.withPostcode / core.totals.records)} of the register
    overall but ${pct(core.totals.oaWithPostcode / core.totals.oa)} of ${gloss('order', 'Order of Australia')}
    records, which is why every geographic measure on this site is an Order of Australia measure.</p>
</div>

<div class="stats">
  <div class="stat" data-tip="${tipAttr(`National rate\n${num(geo.national.awards)} Order of Australia awards since ${geo.fromYear}\n${num(geo.national.pop)} residents (2021 Census)\n= ${dec(natCrude, 2)} per 10,000`)}">
    <div class="stat-value">${dec(natCrude, 2)}</div>
    <div class="stat-label">National rate, awards per 10,000 residents</div>
  </div>
  <div class="stat" data-tip="${tipAttr(`Decile 10: ${dec(d10.crude, 2)} per 10,000\nDecile 1: ${dec(d1.crude, 2)} per 10,000\nRatio ${dec(ratioCrude, 2)}x`)}">
    <div class="stat-value">${dec(ratioCrude, 2)}&times;</div>
    <div class="stat-label">Richest decile vs poorest, crude rate</div>
  </div>
  <div class="stat" data-tip="${tipAttr(`Per 10,000 residents aged 65+\nDecile 10: ${dec(d10.aged, 1)}\nDecile 1: ${dec(d1.aged, 1)}\nRatio ${dec(ratioAged, 2)}x — WIDER than the crude ${dec(ratioCrude, 2)}x`)}">
    <div class="stat-value">${dec(ratioAged, 2)}&times;</div>
    <div class="stat-label">The same ratio after controlling for age — it widens</div>
  </div>
  <div class="stat" data-tip="${tipAttr(`Deciles 1-8 crude rates\nlowest ${dec(lo8, 2)} — highest ${dec(hi8, 2)} per 10,000\nRatio ${dec(spread8, 2)}x across eight tenths of the country`)}">
    <div class="stat-value">${dec(spread8, 2)}&times;</div>
    <div class="stat-label">Total spread across deciles 1&ndash;8 — the flat majority</div>
  </div>
</div>

<section class="panel">
  <div class="panel-head">
    <h2>The curve is a J, not a slope</h2>
    <p class="panel-sub">Postcodes sorted by median weekly household income at the 2021 Census and split
      into ten equal groups of about 134. Each group's Order of Australia awards since ${geo.fromYear} are
      expressed ${gloss('per-capita', 'per 10,000 residents')} and again
      ${gloss('age-controlled', 'per 10,000 residents aged 65+')}. Select a decile to filter the
      leaderboard below.</p>
  </div>
  <div class="controls" role="group" aria-label="Chart mode">
    <div class="control-group">
      <button type="button" data-mode="rate">Rates</button>
      <button type="button" data-mode="index">Compared with the national rate</button>
    </div>
    <span class="lb-count" id="lb-mode-note"></span>
  </div>
  <div id="lb-charts"></div>
  <div class="legend" id="lb-legend"></div>
  <div class="note">
    <p><strong>Read it as a J.</strong> Deciles 1 to 8 all sit between ${dec(lo8, 2)} and ${dec(hi8, 2)}
      awards per 10,000 residents — a spread of just ${dec(spread8, 2)}&times; across eight-tenths of the
      country. Decile 9 is ${dec(deciles[8].crude, 2)} and decile 10 is ${dec(d10.crude, 2)}. The poorest
      decile (${dec(d1.crude, 2)}) out-rates ${beaten.length > 0 ? `deciles ${beaten.join(', ')}` : 'several middle deciles'}.
      So the finding is <em>not</em> that richer postcodes get more honours in proportion to income, and
      <em>not</em> that poor postcodes are systematically overlooked: it is that most of Australia is
      indistinguishable, and the top two deciles are somewhere else entirely.</p>
    <p style="margin-top:0.6rem"><strong>The age objection fails, in the opposite direction.</strong>
      Recipients skew elderly and wealthy suburbs are often assumed to as well, so the gap might be an
      age artefact. It is not. Per 10,000 residents aged 65 and over, the top-to-bottom ratio
      <em>widens</em> from ${dec(ratioCrude, 2)}&times; to ${dec(ratioAged, 2)}&times;
      (decile 1 ${dec(d1.aged, 1)} &rarr; decile 10 ${dec(d10.aged, 1)}), because the richest postcodes
      are in fact <em>younger</em> — a mean median age of ${dec(d10.medAge, 1)} against ${dec(d1.medAge, 1)}
      in the poorest. Controlling for age makes the gap bigger, not smaller.</p>
  </div>
</section>

<section class="panel">
  <div class="panel-head">
    <h2>Every eligible postcode, ranked</h2>
    <p class="panel-sub">${num(rows.length)} postcodes that clear the population floor. Rates are Order of
      Australia awards gazetted from ${geo.fromYear} onwards, over 2021 Census population.
      &ldquo;vs expected&rdquo; is the awards a postcode received minus the number the national rate of
      ${dec(natCrude, 2)} per 10,000 predicts for its population. Select a row to open that postcode on
      the map.</p>
  </div>
  <div class="controls">
    <div class="control-group" role="group" aria-label="Sort the leaderboard">
      <button type="button" data-sort="crude">Per 10,000</button>
      <button type="button" data-sort="aged">Per 10,000 aged 65+</button>
      <button type="button" data-sort="n">Awards</button>
      <button type="button" data-sort="surplus">Most above expected</button>
      <button type="button" data-sort="shortfall">Most below expected</button>
    </div>
    <label class="lb-count" for="lb-q">Find a postcode
      <input type="search" id="lb-q" inputmode="numeric" placeholder="e.g. 3142" style="max-width:9rem"
             aria-label="Filter the leaderboard by postcode" /></label>
    <span id="lb-chip"></span>
  </div>
  <div class="scroller"><table id="lb-table"><thead id="lb-head"></thead><tbody id="lb-body"></tbody></table></div>
  <div id="lb-empty"></div>
  <div class="lb-foot">
    <span class="lb-count" id="lb-showing"></span>
    <button type="button" class="pill lb-pill" id="lb-more">Show 40 more</button>
    <button type="button" class="pill lb-pill" id="lb-all">Show all</button>
  </div>
</section>

<section class="panel">
  <div class="panel-head">
    <h2>The floor, and why there is one</h2>
    <p class="panel-sub">What is excluded from every rate above, and the reason.</p>
  </div>
  <p class="panel-sub">${num(geo.places.length)} postcodes have at least one Order of Australia award
    since ${geo.fromYear} and a published 2021 Census population.
    <strong>${num(eligible.length)}</strong> of them clear the floor of at least
    ${num(geo.minPop)} residents <em>and</em> at least ${num(geo.minOver65)} residents aged 65 and over.
    The other <strong>${num(excluded)}</strong> — holding ${num(excludedAwards)} awards and
    ${num(excludedPop)} residents between them — are excluded from every rate on this page.</p>
  <div class="lb-key">
    <div>${
      tiny
        ? `<strong>Why:</strong> without a floor, postcode ${escapeHtml(tiny.pc)} tops the country —
           ${num(tiny.n)} award${tiny.n === 1 ? '' : 's'} among ${num(tiny.pop)} residents is
           ${dec(tiny.rate, 1)} per 10,000, about ${dec(bestEligible > 0 ? tiny.rate / bestEligible : 0, 1)}&times;
           the highest eligible postcode (${dec(bestEligible, 1)}). One award in a place of a few dozen
           people is noise, not a finding.`
        : '<strong>Why:</strong> in a very small postcode a single award produces an enormous rate, which is noise rather than a finding.'
    }</div>
    <div><strong>Income deciles</strong> cover ${num(dmap ? dmap.size : deciles.reduce((s, d) => s + d.places, 0))}
      postcodes: one eligible postcode has no published median household income or median age, so it is
      ranked in the table but carries no decile.</div>
  </div>
  <div class="note">These are rates over <em>resident</em> population, not over nominations. The register
    publishes no nomination data, so nothing here can separate who is nominated from who is selected.
    A ${gloss('service-domain', 'service domain')} breakdown of the same awards lives on the
    &ldquo;What for&rdquo; page.</div>
</section>`;

  // ---------------------------------------------------------------- charts --
  const chartHost = root.querySelector<HTMLDivElement>('#lb-charts')!;
  const legendHost = root.querySelector<HTMLDivElement>('#lb-legend')!;
  const modeNote = root.querySelector<HTMLSpanElement>('#lb-mode-note')!;

  function decileTip(i: number): string {
    const d = deciles[i];
    return [
      `Income decile ${d.decile} of 10 — ${num(d.places)} postcodes`,
      `${num(d.awards)} Order of Australia awards since ${geo.fromYear}`,
      `${num(d.pop)} residents, of whom ${num(d.over65)} are 65+`,
      `${dec(d.crude, 2)} per 10,000 residents (national ${dec(natCrude, 2)})`,
      `${dec(d.aged, 1)} per 10,000 aged 65+ (national ${dec(natAged, 2)})`,
      `Mean median household income ${money(d.inc)}/week; mean median age ${dec(d.medAge, 1)}`,
      dmap ? 'Select to filter the leaderboard below' : '',
    ]
      .filter(Boolean)
      .join('\n');
  }

  function markSelectable(node: SVGElement, dnum: number): void {
    node.setAttribute('data-decile', String(dnum));
    if (!dmap) return;
    node.setAttribute('tabindex', '0');
    node.setAttribute('role', 'button');
    node.style.cursor = 'pointer';
  }

  function barPanel(kind: 'crude' | 'aged'): HTMLElement {
    const W = 480;
    const H = 336;
    const M = { top: 20, right: 14, bottom: 82, left: 54 };
    const innerW = W - M.left - M.right;
    const innerH = H - M.top - M.bottom;
    const band = innerW / Math.max(1, deciles.length);
    const bw = Math.max(6, band * 0.62);

    const values = deciles.map((d) => (kind === 'crude' ? d.crude : d.aged));
    const refValue = kind === 'crude' ? natCrude : natAged;
    const { max, step } = axisTop(Math.max(...values, refValue), 5);
    const yOf = (v: number): number =>
      M.top + innerH - (Number.isFinite(v) && max > 0 ? (Math.max(0, v) / max) * innerH : 0);

    const wrap = document.createElement('div');
    wrap.innerHTML = `<div class="lb-chart-title">${
      kind === 'crude' ? 'Crude rate' : 'Age-controlled rate'
    }</div><div class="lb-chart-sub">${
      kind === 'crude'
        ? `Order of Australia awards since ${geo.fromYear} per 10,000 residents of any age.`
        : `The same awards per 10,000 residents aged 65 and over — the age control.`
    }</div>`;

    const frame = document.createElement('div');
    frame.className = 'chart-wrap lb-chart-wrap';
    const svg = svgEl('svg', {
      class: 'chart',
      viewBox: `0 0 ${W} ${H}`,
      role: 'group',
      'aria-label': `${kind === 'crude' ? 'Crude' : 'Age-controlled'} Order of Australia award rate by postcode income decile, bar chart`,
    });

    for (const t of ticksOf(max, step)) {
      const y = yOf(t);
      svg.appendChild(
        svgEl('line', {
          x1: M.left,
          x2: M.left + innerW,
          y1: y,
          y2: y,
          stroke: 'var(--border-subtle)',
          'stroke-width': 1,
        }),
      );
      svg.appendChild(
        svgText(M.left - 7, y + 4, dec(t, 0), {
          'font-size': 11,
          'text-anchor': 'end',
        }),
      );
    }

    // Bars.
    deciles.forEach((d, i) => {
      const v = values[i];
      const x = M.left + i * band + (band - bw) / 2;
      const y = yOf(v);
      const h = Math.max(1, M.top + innerH - y);
      const g = svgEl('g');
      const rect = svgEl('rect', {
        x,
        y,
        width: bw,
        height: h,
        rx: 2,
        fill: kind === 'crude' ? CRUDE_COLOUR : AGED_COLOUR,
      });
      rect.setAttribute('data-tip', tipValue(decileTip(i)));
      rect.setAttribute(
        'aria-label',
        `Decile ${d.decile}, ${dec(v, 2)} awards per 10,000 ${kind === 'crude' ? 'residents' : 'residents aged 65 plus'}`,
      );
      markSelectable(rect, d.decile);
      g.appendChild(rect);
      g.appendChild(
        svgText(x + bw / 2, y - 5, dec(v, kind === 'crude' ? 2 : 1), {
          'font-size': 11,
          'text-anchor': 'middle',
          fill: 'var(--text-secondary)',
        }),
      );
      g.setAttribute('data-decile-group', String(d.decile));
      svg.appendChild(g);
    });

    // Axis line + decile / income labels.
    svg.appendChild(
      svgEl('line', {
        x1: M.left,
        x2: M.left + innerW,
        y1: M.top + innerH,
        y2: M.top + innerH,
        stroke: 'var(--border-default)',
        'stroke-width': 1,
      }),
    );
    deciles.forEach((d, i) => {
      const cx = M.left + i * band + band / 2;
      svg.appendChild(
        svgText(cx, M.top + innerH + 17, String(d.decile), {
          'font-size': 12.5,
          'text-anchor': 'middle',
          fill: 'var(--text-primary)',
        }),
      );
      svg.appendChild(
        svgText(cx, M.top + innerH + 31, money(d.inc), {
          'font-size': 10.5,
          'text-anchor': 'middle',
        }),
      );
    });
    svg.appendChild(
      svgText(M.left + innerW / 2, M.top + innerH + 52, 'Income decile (1 = lowest-income tenth)', {
        'font-size': 11.5,
        'text-anchor': 'middle',
      }),
    );
    svg.appendChild(
      svgText(M.left + innerW / 2, M.top + innerH + 66, 'and its mean median weekly household income', {
        'font-size': 11.5,
        'text-anchor': 'middle',
      }),
    );
    const yTitle = svgText(
      0,
      0,
      kind === 'crude' ? 'Awards per 10,000 residents' : 'Awards per 10,000 aged 65+',
      { 'font-size': 11.5, 'text-anchor': 'middle', transform: `translate(14, ${M.top + innerH / 2}) rotate(-90)` },
    );
    svg.appendChild(yTitle);

    // National reference line, drawn last so it sits above the bars.
    const refY = yOf(refValue);
    const ref = svgEl('g');
    const refLine = svgEl('line', {
      x1: M.left,
      x2: M.left + innerW,
      y1: refY,
      y2: refY,
      stroke: 'var(--accent-secondary)',
      'stroke-width': 1.6,
      'stroke-dasharray': '6 4',
    });
    const refHit = svgEl('rect', {
      x: M.left,
      y: refY - 7,
      width: innerW,
      height: 14,
      fill: 'transparent',
      'pointer-events': 'all',
    });
    refHit.setAttribute(
      'data-tip',
      tipValue(
        `National rate: ${dec(refValue, 2)} per 10,000 ${kind === 'crude' ? 'residents' : 'residents aged 65+'}\n${num(geo.national.awards)} awards over ${num(kind === 'crude' ? geo.national.pop : geo.national.over65)} people`,
      ),
    );
    refHit.setAttribute('aria-label', `National rate ${dec(refValue, 2)} per 10,000`);
    ref.append(refLine, refHit);
    ref.appendChild(
      svgText(M.left + innerW, refY - 6, `National ${dec(refValue, 2)}`, {
        'font-size': 11,
        'text-anchor': 'end',
        fill: 'var(--accent-secondary)',
      }),
    );
    svg.appendChild(ref);

    frame.appendChild(svg);
    wrap.appendChild(frame);
    return wrap;
  }

  function dumbbellPanel(): HTMLElement {
    const W = 480;
    const H = 392;
    const M = { top: 52, right: 18, bottom: 44, left: 96 };
    const innerW = W - M.left - M.right;
    const innerH = H - M.top - M.bottom;
    const rowH = innerH / Math.max(1, deciles.length);

    const idxCrude = deciles.map((d) => (natCrude > 0 ? d.crude / natCrude : 0));
    const idxAged = deciles.map((d) => (natAged > 0 ? d.aged / natAged : 0));
    const { max, step } = axisTop(Math.max(...idxCrude, ...idxAged, 1), 6);
    const xOf = (v: number): number =>
      M.left + (Number.isFinite(v) && max > 0 ? (Math.max(0, v) / max) * innerW : 0);

    const wrap = document.createElement('div');
    wrap.innerHTML = `<div class="lb-chart-title">Each decile compared with the national rate</div>
      <div class="lb-chart-sub">Both measures divided by their own national rate, so they share one axis.
        1.0 = exactly the national rate. The gap between the two dots is what the age control does:
        it pushes the poorest deciles down and the richest up.</div>`;

    const frame = document.createElement('div');
    frame.className = 'chart-wrap lb-chart-wrap';
    const svg = svgEl('svg', {
      class: 'chart',
      viewBox: `0 0 ${W} ${H}`,
      role: 'group',
      'aria-label':
        'Dumbbell chart: crude and age-controlled Order of Australia award rates by income decile, as a multiple of the national rate',
    });

    for (const t of ticksOf(max, step)) {
      const x = xOf(t);
      svg.appendChild(
        svgEl('line', {
          x1: x,
          x2: x,
          y1: M.top,
          y2: M.top + innerH,
          stroke: 'var(--border-subtle)',
          'stroke-width': 1,
        }),
      );
      svg.appendChild(
        svgText(x, M.top - 10, `${dec(t, 1)}×`, { 'font-size': 11, 'text-anchor': 'middle' }),
      );
    }
    svg.appendChild(
      svgText(M.left + innerW / 2, M.top - 30, 'Award rate as a multiple of the national rate', {
        'font-size': 11.5,
        'text-anchor': 'middle',
      }),
    );

    // The 1.0 reference.
    const refX = xOf(1);
    svg.appendChild(
      svgEl('line', {
        x1: refX,
        x2: refX,
        y1: M.top - 4,
        y2: M.top + innerH + 4,
        stroke: 'var(--accent-secondary)',
        'stroke-width': 1.6,
        'stroke-dasharray': '6 4',
      }),
    );
    const refHit = svgEl('rect', {
      x: refX - 7,
      y: M.top - 4,
      width: 14,
      height: innerH + 8,
      fill: 'transparent',
      'pointer-events': 'all',
    });
    refHit.setAttribute(
      'data-tip',
      tipValue(
        `The national rate\n${dec(natCrude, 2)} per 10,000 residents\n${dec(natAged, 2)} per 10,000 aged 65+`,
      ),
    );
    refHit.setAttribute('aria-label', 'The national rate, 1.0 times');
    svg.appendChild(refHit);

    deciles.forEach((d, i) => {
      const yTop = M.top + i * rowH;
      const cy = yTop + rowH / 2;
      const g = svgEl('g', { 'data-decile-group': String(d.decile) });

      const hit = svgEl('rect', {
        x: M.left,
        y: yTop,
        width: innerW,
        height: rowH,
        fill: 'transparent',
        'pointer-events': 'all',
      });
      hit.setAttribute('data-tip', tipValue(decileTip(i)));
      hit.setAttribute(
        'aria-label',
        `Decile ${d.decile}: crude ${dec(idxCrude[i], 2)} times the national rate, age-controlled ${dec(idxAged[i], 2)} times`,
      );
      markSelectable(hit, d.decile);
      g.appendChild(hit);

      g.appendChild(
        svgEl('line', {
          x1: xOf(idxCrude[i]),
          x2: xOf(idxAged[i]),
          y1: cy,
          y2: cy,
          stroke: 'var(--border-strong)',
          'stroke-width': 2.5,
          'pointer-events': 'none',
        }),
      );
      g.appendChild(
        svgEl('circle', {
          cx: xOf(idxCrude[i]),
          cy,
          r: 6,
          fill: CRUDE_COLOUR,
          stroke: 'var(--bg-panel)',
          'stroke-width': 1.2,
          'pointer-events': 'none',
        }),
      );
      g.appendChild(
        svgEl('circle', {
          cx: xOf(idxAged[i]),
          cy,
          r: 6,
          fill: AGED_COLOUR,
          stroke: 'var(--bg-panel)',
          'stroke-width': 1.2,
          'pointer-events': 'none',
        }),
      );
      g.appendChild(
        svgText(M.left - 10, cy + 4, `${d.decile} · ${money(d.inc)}`, {
          'font-size': 11,
          'text-anchor': 'end',
          fill: 'var(--text-primary)',
        }),
      );
      svg.appendChild(g);
    });

    svg.appendChild(
      svgText(M.left + innerW / 2, M.top + innerH + 30, 'Income decile and its mean median weekly household income', {
        'font-size': 11.5,
        'text-anchor': 'middle',
      }),
    );

    frame.appendChild(svg);
    wrap.appendChild(frame);
    return wrap;
  }

  function drawLegend(): void {
    const items =
      state.mode === 'rate'
        ? [
            [CRUDE_COLOUR, `Crude rate — awards per 10,000 residents (national ${dec(natCrude, 2)})`],
            [AGED_COLOUR, `Age-controlled — per 10,000 aged 65+ (national ${dec(natAged, 2)})`],
          ]
        : [
            [CRUDE_COLOUR, 'Crude rate, as a multiple of the national crude rate'],
            [AGED_COLOUR, 'Age-controlled rate, as a multiple of the national 65+ rate'],
          ];
    legendHost.innerHTML =
      items
        .map(
          ([c, label]) =>
            `<span class="legend-item"><span class="swatch" style="background:${c}"></span><span>${escapeHtml(label)}</span></span>`,
        )
        .join('') +
      `<span class="legend-item"><span class="lb-dash"></span><span>${
        state.mode === 'rate' ? 'National rate' : 'The national rate (1.0×)'
      }</span></span>`;
  }

  function paintSelection(): void {
    for (const g of Array.from(root.querySelectorAll<SVGGElement>('[data-decile-group]'))) {
      const d = Number(g.getAttribute('data-decile-group'));
      const dim = state.decile != null && d !== state.decile;
      g.setAttribute('opacity', dim ? '0.26' : '1');
      const marks = g.querySelectorAll<SVGElement>('rect, circle');
      for (const m of Array.from(marks)) {
        if (state.decile != null && d === state.decile) m.setAttribute('stroke', 'var(--accent-secondary)');
        else if (m.tagName === 'circle') m.setAttribute('stroke', 'var(--bg-panel)');
        else m.removeAttribute('stroke');
        if (state.decile != null && d === state.decile) m.setAttribute('stroke-width', '2');
      }
    }
  }

  function drawCharts(): void {
    chartHost.innerHTML = '';
    if (state.mode === 'rate') {
      const grid = document.createElement('div');
      grid.className = 'lb-grid';
      grid.append(barPanel('crude'), barPanel('aged'));
      chartHost.appendChild(grid);
    } else {
      chartHost.appendChild(dumbbellPanel());
    }
    for (const b of Array.from(root.querySelectorAll<HTMLButtonElement>('[data-mode]'))) {
      b.setAttribute('aria-pressed', String(b.dataset.mode === state.mode));
    }
    modeNote.textContent = dmap
      ? 'Select any decile to filter the leaderboard.'
      : 'Decile filtering is unavailable: the rebuilt deciles did not reconcile with the published totals.';
    drawLegend();
    paintSelection();
  }

  // ----------------------------------------------------------------- table --
  const headHost = root.querySelector<HTMLTableSectionElement>('#lb-head')!;
  const bodyHost = root.querySelector<HTMLTableSectionElement>('#lb-body')!;
  const emptyHost = root.querySelector<HTMLDivElement>('#lb-empty')!;
  const showingHost = root.querySelector<HTMLSpanElement>('#lb-showing')!;
  const chipHost = root.querySelector<HTMLSpanElement>('#lb-chip')!;
  const moreBtn = root.querySelector<HTMLButtonElement>('#lb-more')!;
  const allBtn = root.querySelector<HTMLButtonElement>('#lb-all')!;
  const qInput = root.querySelector<HTMLInputElement>('#lb-q')!;
  qInput.value = state.q;

  const COLUMNS: { key: SortKey | null; label: string; n: boolean }[] = [
    { key: null, label: '#', n: true },
    { key: null, label: 'Postcode', n: false },
    { key: null, label: 'Decile', n: true },
    { key: 'n', label: 'Awards', n: true },
    { key: null, label: 'Residents', n: true },
    { key: 'crude', label: 'Per 10,000', n: true },
    { key: 'aged', label: 'Per 10,000 65+', n: true },
    { key: null, label: 'Income/week', n: true },
    { key: null, label: 'Median age', n: true },
    { key: 'surplus', label: 'vs expected', n: true },
  ];

  function drawHead(): void {
    headHost.innerHTML = `<tr>${COLUMNS.map((c) => {
      const active = c.key != null && (state.sort === c.key || (c.key === 'surplus' && state.sort === 'shortfall'));
      const sortAttr = active
        ? ` aria-sort="${state.sort === 'shortfall' ? 'ascending' : 'descending'}"`
        : '';
      const inner =
        c.key == null
          ? escapeHtml(c.label)
          : `<button type="button" class="lb-th" data-sort="${c.key}" aria-pressed="${active}">${escapeHtml(c.label)}${active ? (state.sort === 'shortfall' ? ' ▴' : ' ▾') : ''}</button>`;
      return `<th class="${c.n ? 'n' : ''}"${sortAttr}>${inner}</th>`;
    }).join('')}</tr>`;
  }

  function compare(a: Row, b: Row): number {
    const val = (r: Row, k: SortKey): number => {
      if (k === 'crude') return r.crude;
      if (k === 'aged') return Number.isFinite(r.aged) ? r.aged : -1;
      if (k === 'n') return r.n;
      return r.surplus;
    };
    if (state.sort === 'shortfall') return val(a, 'surplus') - val(b, 'surplus');
    return val(b, state.sort) - val(a, state.sort) || b.crude - a.crude;
  }

  function visibleRows(): Row[] {
    let out = rows;
    if (state.decile != null) out = out.filter((r) => r.decile === state.decile);
    if (state.q) out = out.filter((r) => r.pc.startsWith(state.q));
    return out.slice().sort(compare);
  }

  function rowTip(r: Row, rank: number): string {
    return [
      `#${rank} — postcode ${r.pc}${r.decile != null ? `, income decile ${r.decile} of 10` : ', no published income decile'}`,
      `${num(r.n)} Order of Australia award${r.n === 1 ? '' : 's'} since ${geo.fromYear}`,
      `${num(r.pop)} residents, of whom ${num(r.o65)} are 65+`,
      `${dec(r.crude, 2)} per 10,000 residents (national ${dec(natCrude, 2)})`,
      `${Number.isFinite(r.aged) ? dec(r.aged, 1) : '—'} per 10,000 aged 65+ (national ${dec(natAged, 2)})`,
      `Median household income ${r.inc != null ? `${money(r.inc)}/week` : 'not published'}; median age ${r.age != null ? dec(r.age, 0) : 'not published'}`,
      `${signed(r.surplus)} awards against the ${dec((r.pop / 1e4) * natCrude, 1)} the national rate predicts`,
      'Select to open this postcode on the map',
    ].join('\n');
  }

  function drawTable(): void {
    const list = visibleRows();
    const shown = list.slice(0, state.limit);

    bodyHost.innerHTML = shown
      .map((r, i) => {
        const rank = i + 1;
        const barW = maxCrudeRow > 0 ? Math.max(1, Math.round((r.crude / maxCrudeRow) * 100)) : 0;
        const sColour = r.surplus >= 0 ? DIVERGING.high : DIVERGING.low;
        return `<tr data-row data-pc="${escapeHtml(r.pc)}" data-tip="${tipAttr(rowTip(r, rank))}">
  <td class="n">${rank}</td>
  <td><button type="button" class="lb-pc" data-pc="${escapeHtml(r.pc)}"
        aria-label="Postcode ${escapeHtml(r.pc)}: ${dec(r.crude, 2)} awards per 10,000 residents. Open on the map">${escapeHtml(r.pc)}</button></td>
  <td class="n">${r.decile != null ? r.decile : '—'}</td>
  <td class="n">${num(r.n)}</td>
  <td class="n">${num(r.pop)}</td>
  <td class="n lb-rowbar"><span>${dec(r.crude, 2)}</span><span class="lb-bar" style="width:${barW}%"></span></td>
  <td class="n">${Number.isFinite(r.aged) ? dec(r.aged, 1) : '—'}</td>
  <td class="n">${r.inc != null ? money(r.inc) : '—'}</td>
  <td class="n">${r.age != null ? dec(r.age, 0) : '—'}</td>
  <td class="n" style="color:${sColour}">${signed(r.surplus)}</td>
</tr>`;
      })
      .join('');

    emptyHost.innerHTML = list.length
      ? ''
      : `<p class="empty">No postcode here matches ${
          state.q ? `“${escapeHtml(state.q)}”` : 'that filter'
        }${state.decile != null ? ` in decile ${state.decile}` : ''}.</p>`;

    showingHost.textContent = `Showing ${num(Math.min(state.limit, list.length))} of ${num(list.length)} postcodes${
      state.decile != null ? ` in decile ${state.decile}` : ''
    } · Order of Australia, ${geo.fromYear} onwards`;
    moreBtn.hidden = list.length <= state.limit;
    allBtn.hidden = list.length <= state.limit;

    chipHost.innerHTML =
      state.decile != null
        ? `<button type="button" class="pill lb-pill" id="lb-clear"
             data-tip="${tipAttr(`Showing only the ${num(deciles[state.decile - 1].places)} postcodes in income decile ${state.decile}\nMean median household income ${money(deciles[state.decile - 1].inc)}/week`)}">
             Decile ${state.decile} only ×</button>`
        : '';
    drawHead();
  }

  // --------------------------------------------------------------- events --
  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    const modeBtn = target.closest<HTMLButtonElement>('[data-mode]');
    if (modeBtn) {
      state.mode = modeBtn.dataset.mode === 'index' ? 'index' : 'rate';
      drawCharts();
      return;
    }

    const mark = target.closest<Element>('[data-decile]');
    if (mark && dmap) {
      const d = Number(mark.getAttribute('data-decile'));
      state.decile = state.decile === d ? null : d;
      state.limit = 40;
      paintSelection();
      drawTable();
      return;
    }

    if (target.closest('#lb-clear')) {
      state.decile = null;
      state.limit = 40;
      paintSelection();
      drawTable();
      return;
    }

    const sortBtn = target.closest<HTMLButtonElement>('[data-sort]');
    if (sortBtn) {
      const key = sortBtn.dataset.sort as SortKey;
      state.sort = state.sort === 'surplus' && key === 'surplus' ? 'shortfall' : key;
      state.limit = 40;
      drawTable();
      for (const b of Array.from(root.querySelectorAll<HTMLButtonElement>('.control-group [data-sort]'))) {
        b.setAttribute('aria-pressed', String(b.dataset.sort === state.sort));
      }
      return;
    }

    const pcBtn = target.closest<HTMLElement>('[data-pc]');
    if (pcBtn) {
      ctx.go('where', { pc: pcBtn.dataset.pc ?? '' });
      return;
    }

    if (target.closest('#lb-more')) {
      state.limit += 40;
      drawTable();
      return;
    }
    if (target.closest('#lb-all')) {
      state.limit = rows.length;
      drawTable();
    }
  });

  // Keyboard equivalents for the SVG marks, which are focusable but not buttons.
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const mark = (e.target as Element).closest<Element>('[data-decile]');
    if (!mark || !dmap) return;
    e.preventDefault();
    const d = Number(mark.getAttribute('data-decile'));
    state.decile = state.decile === d ? null : d;
    state.limit = 40;
    paintSelection();
    drawTable();
  });

  qInput.addEventListener('input', () => {
    state.q = qInput.value.replace(/[^0-9]/g, '').slice(0, 4);
    if (qInput.value !== state.q) qInput.value = state.q;
    state.limit = 40;
    drawTable();
  });

  for (const b of Array.from(root.querySelectorAll<HTMLButtonElement>('.control-group [data-sort]'))) {
    b.setAttribute('aria-pressed', String(b.dataset.sort === state.sort));
  }

  drawCharts();
  drawTable();
};
